import { InteractiveContainer, PlainButton } from "@jugl-web/ui-kit";
import {
  cx,
  getUniqueId,
  onEnter,
  onEscape,
  reorder,
  useTranslations,
} from "@jugl-web/utils";
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  MouseSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Dispatch,
  FC,
  forwardRef,
  ReactNode,
  SetStateAction,
  useCallback,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useEffectOnce } from "react-use";
import { CUSTOM_DROPDOWN_VALUE_CHAR_LIMIT } from "../../consts";
import { customDropdownValueValidator } from "../../utils";
import { ReactComponent as ReorderIcon } from "../DraggableFieldBoxContainer/assets/reorder.svg";
import { ReactComponent as TrashBinIcon } from "./assets/trash-bin.svg";

export interface DropdownValueListItem {
  id: string;
  text: string;
  color: string;
}

interface SortableFieldBoxContainerProps {
  id: string;
  index: number;
  className?: string;
  children: ReactNode;
}

const SortableFieldBoxContainer: FC<SortableFieldBoxContainerProps> = ({
  id,
  index,
  className,
  children,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id,
    data: { index },
  });

  return (
    <div
      ref={setNodeRef}
      style={{
        opacity: isDragging ? 0 : undefined,
        transform: CSS.Translate.toString(transform),
        transition,
      }}
      className={cx("relative", className)}
    >
      <PlainButton
        ref={setActivatorNodeRef}
        className="absolute top-1/2 -left-0.5 -translate-x-full -translate-y-1/2 cursor-grab active:cursor-grabbing"
        onClick={(event) => event.stopPropagation()}
        {...attributes}
        {...listeners}
      >
        <ReorderIcon />
      </PlainButton>
      {children}
    </div>
  );
};

interface DropdownValueListItemRowProps {
  item: DropdownValueListItem;
  isEditing: boolean;
  onStartEditing?: () => void;
  onFinishEditing?: () => void;
  onUpdate?: (text: string) => void;
  onRemove?: () => void;
}

const DropdownValueListItemRow: FC<DropdownValueListItemRowProps> = ({
  item,
  isEditing,
  onStartEditing,
  onFinishEditing,
  onUpdate,
  onRemove,
}) => {
  const [internalText, setInternalText] = useState(item.text);
  const { t } = useTranslations();

  const isValid = customDropdownValueValidator(internalText);

  const handleEditingFinished = useCallback(() => {
    if (!isValid) {
      onRemove?.();
      return;
    }

    onUpdate?.(internalText);
    // oxlint-disable-next-line react/exhaustive-deps
  }, [internalText, isValid]);

  useLayoutEffect(() => {
    if (!isEditing) {
      handleEditingFinished();
    }
  }, [handleEditingFinished, isEditing]);

  return (
    <InteractiveContainer
      className={cx(
        "relative flex h-[56px] cursor-pointer! items-center justify-between rounded-lg px-6 transition-colors",
        isEditing ? "bg-[#F6F6F7]" : "bg-grey-100 hover:bg-[#F6F6F7]"
      )}
      onClick={onStartEditing}
    >
      <div className="grow">
        {isEditing ? (
          <div className="relative mr-10">
            <input
              type="text"
              autoFocus
              maxLength={CUSTOM_DROPDOWN_VALUE_CHAR_LIMIT}
              placeholder={t({
                id: "form.controls.custom-dropdown-field-value.placeholder",
                defaultMessage: "Enter value name",
              })}
              className="w-full border-t-0 border-r-0 border-b border-l-0 border-b-[#E0E0E0] bg-transparent px-0 py-[6.5px] font-[Roboto] text-base text-dark outline-none placeholder:text-grey-600"
              value={internalText}
              onChange={(event) => setInternalText(event.target.value)}
              onKeyUp={onEnter(() => isValid && onFinishEditing?.())}
              onKeyDown={onEscape((event) => {
                event.stopPropagation();
                onFinishEditing?.();
              })}
              onBlur={onFinishEditing}
            />
            <span className="pointer-events-none absolute top-1/2 right-0 -translate-y-1/2 text-grey-600">
              {internalText.length}/{CUSTOM_DROPDOWN_VALUE_CHAR_LIMIT}
            </span>
          </div>
        ) : (
          <span className="text-base text-dark">{item.text}</span>
        )}
      </div>
      {onRemove && (
        <PlainButton
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition-colors hover:bg-grey-200"
          onClick={(event) => {
            event.stopPropagation();
            onRemove();
          }}
        >
          <TrashBinIcon />
        </PlainButton>
      )}
    </InteractiveContainer>
  );
};

interface DropdownValueListItemComponentProps {
  item: DropdownValueListItem;
  index: number;
  isEditing: boolean;
  onStartEditing: () => void;
  onFinishEditing: () => void;
  onUpdate: (text: string) => void;
  onRemove: () => void;
}

const DropdownValueListItemComponent: FC<
  DropdownValueListItemComponentProps
> = ({
  item,
  index,
  isEditing,
  onStartEditing,
  onFinishEditing,
  onUpdate,
  onRemove,
}) => (
  <SortableFieldBoxContainer id={item.id} index={index} className="mb-4">
    <DropdownValueListItemRow
      item={item}
      isEditing={isEditing}
      onStartEditing={onStartEditing}
      onFinishEditing={onFinishEditing}
      onUpdate={onUpdate}
      onRemove={onRemove}
    />
  </SortableFieldBoxContainer>
);

interface DropdownValuesListProps {
  items: DropdownValueListItem[];
  onChange: Dispatch<SetStateAction<DropdownValueListItem[]>>;
  autoAddItem?: boolean;
  className?: string;
}

export interface DropdownValuesListHandle {
  addItem: () => void;
}

export const DropdownValueList = forwardRef<
  DropdownValuesListHandle,
  DropdownValuesListProps
>(({ items, onChange, autoAddItem, className }, ref) => {
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [activeDraggableId, setActiveDraggableId] = useState<string | null>(
    null
  );

  const activeDraggableItem = useMemo(
    () => items.find((item) => item.id === activeDraggableId),
    [activeDraggableId, items]
  );

  const sortableItemIds = useMemo(() => items.map((item) => item.id), [items]);

  const sensors = useSensors(useSensor(MouseSensor));

  const addItem = useCallback(() => {
    const newItem: DropdownValueListItem = {
      id: getUniqueId(),
      text: "",
      color: "#CCCCCC",
    };

    onChange((prevItems) => [...prevItems, newItem]);
    setEditingItemId(newItem.id);
  }, [onChange]);

  useImperativeHandle(ref, () => ({ addItem }));

  const handleUpdateItem = (id: string, text: string) => {
    onChange((prevItems) =>
      prevItems.map((item) => (item.id === id ? { ...item, text } : item))
    );
  };

  const handleRemoveItem = (id: string) => {
    onChange((prevItems) => prevItems.filter((item) => item.id !== id));
  };

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      const activeItem = items.find((item) => item.id === event.active.id);

      if (activeItem) {
        setActiveDraggableId(activeItem.id);
      }
    },
    [items]
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setActiveDraggableId(null);

      const { active, over } = event;
      const activeIndex = active.data.current?.index;
      const overIndex = over?.data.current?.index;

      if (
        typeof activeIndex !== "number" ||
        typeof overIndex !== "number" ||
        activeIndex === overIndex
      ) {
        return;
      }

      onChange((prevItems) => reorder(prevItems, activeIndex, overIndex));
    },
    [onChange]
  );

  useEffectOnce(() => {
    if (autoAddItem && items.length === 0) {
      addItem();
    }
  });

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={sortableItemIds}
        strategy={verticalListSortingStrategy}
      >
        <div className={cx("flex flex-col", className)}>
          {items.map((item, index) => (
            <DropdownValueListItemComponent
              key={item.id}
              item={item}
              index={index}
              isEditing={editingItemId === item.id}
              onStartEditing={() => setEditingItemId(item.id)}
              onFinishEditing={() => setEditingItemId(null)}
              onUpdate={(text) => handleUpdateItem(item.id, text)}
              onRemove={() => handleRemoveItem(item.id)}
            />
          ))}
        </div>
      </SortableContext>
      {createPortal(
        <DragOverlay style={{ zIndex: 2001 }}>
          {activeDraggableItem && (
            <div className="relative mb-4">
              <PlainButton className="absolute top-1/2 -left-0.5 -translate-x-full -translate-y-1/2 cursor-grab">
                <ReorderIcon />
              </PlainButton>
              <div className="relative flex h-[56px] items-center justify-between rounded-lg bg-grey-100 px-6">
                <span className="text-base text-dark">
                  {activeDraggableItem.text}
                </span>
              </div>
            </div>
          )}
        </DragOverlay>,
        document.body
      )}
    </DndContext>
  );
});
